import { DownOutlined } from '@ant-design/icons'
import { useEventEmitter, usePrevious } from 'ahooks'
import { Dropdown, Menu } from 'antd'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  PaginationInput,
  ParentCommentsFragment,
  PostType,
  RequestedPostFragment,
  useCommentUpdatedSubscription,
  useGetPostCommentsQuery,
} from '@/generated/graphql'

import { PostCommentCreate } from './post-comment-create.component'
import { PostComment, SortType } from './post-comment.component'
import { useCommentAdded, useCommentDeleted } from './post-comment.hooks'
import s from './post-comments.module.less'

interface PostCommentsProps {
  post: RequestedPostFragment & ParentCommentsFragment
  showCreateComment: boolean
  showMoreComment: boolean
  loadAndShowComment: boolean
  isCommentControllable?: boolean
  disableCreate?: boolean
  parentCommentPagination?: PaginationInput
  childCommentPagination?: PaginationInput
}

export const PostComments: React.FC<PostCommentsProps> = ({
  post,
  showCreateComment,
  showMoreComment,
  loadAndShowComment,
  isCommentControllable = false,
  disableCreate = false,
  parentCommentPagination,
  childCommentPagination,
}) => {
  if (isCommentControllable && !showMoreComment) {
    if (!post.isCommentDisplayable) {
      return null
    }
  }
  const [t] = useTranslation()
  const [sort, setSort] = React.useState<SortType>('likedCount')
  const previous = usePrevious(sort)
  const { fetchMore } = useGetPostCommentsQuery({
    skip: true,
    variables: {
      postId: post.postId,
      parentCommentPagination: {
        last: 2,
      },
    },
  })
  const onChangeContent$ = useEventEmitter<string | null>()
  const isForumPost = post.postType === PostType.Forum

  useCommentAdded({
    postId: post.postId,
  })

  useCommentUpdatedSubscription({
    variables: {
      postId: post.postId,
    },
  })

  useCommentDeleted({
    postId: post.postId,
  })

  React.useEffect(() => {
    if (loadAndShowComment) {
      onLoadMoreComments()
    }
  }, [loadAndShowComment])

  React.useEffect(() => {
    if (previous) {
      onLoadComments()
    }
  }, [sort])

  const { edges, pageInfo, totalCount } = post.comments

  // 展开其他 N 条评论
  const onLoadMoreComments = () => {
    fetchMore({
      variables: {
        postId: post.postId,
        parentCommentPagination: {
          last: totalCount - edges.length,
          before: pageInfo.startCursor,
          ...(isForumPost && { sortBy: sort }),
        },
      },
    })
  }

  // 展开所有评论
  const onLoadComments = () => {
    fetchMore({
      variables: {
        postId: post.postId,
        parentCommentPagination: {
          last: parentCommentPagination?.last || totalCount,
          ...(isForumPost && { sortBy: sort }),
        },
      },
    })
  }

  const moreComments = totalCount - edges.length > 0 && (
    <div className={cls(s.moreComments, s.actionButton)} onClick={onLoadMoreComments}>
      {t('comment.other.comment', { count: totalCount - edges.length })}
    </div>
  )

  const parentComments = edges
    .map(({ node }) => node)
    .map((c) => (
      <PostComment
        key={c.commentId}
        post={post}
        comment={c}
        childCommentPagination={childCommentPagination}
        sort={sort}
        disableCreate={disableCreate}
      />
    ))

  if (!showCreateComment && edges.length === 0) {
    return null
  }

  if (isForumPost) {
    const sortMenu = (
      <Menu onClick={({ key }) => setSort(key as SortType)}>
        <Menu.Item key='likedCount'>{t('group.likedCount')}</Menu.Item>
        <Menu.Item key='createdAt'>{t('group.createdAt')}</Menu.Item>
      </Menu>
    )

    const dropdown = (
      <div className={s.dropdown}>
        <Dropdown overlay={sortMenu} trigger={['click']}>
          <a>
            {t(`group.${sort}`)} <DownOutlined className={s.dropdownIcon} />
          </a>
        </Dropdown>
      </div>
    )

    return (
      <div className={s.commentsContainer}>
        {!disableCreate && (
          <PostCommentCreate post={post} onChangeContent$={onChangeContent$} placeholder={t('comment.ask')} />
        )}
        {dropdown}
        {parentComments.slice().reverse()}
        {moreComments}
      </div>
    )
  }

  return (
    <div className={s.commentsContainer}>
      {moreComments}
      {parentComments}
      {!disableCreate && (
        <PostCommentCreate post={post} onChangeContent$={onChangeContent$} placeholder={t('post.writeComment')} />
      )}
    </div>
  )
}
